import React, { useState, useEffect } from 'react';
import { FileText, Loader2, ChevronRight, Download, Star, TrendingUp, Award, Flame, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BookMockup, getBadge } from '../components/BookMockup';
import { ExitIntentPopup } from '../components/ExitIntentPopup';
import { HorizontalScroll, ScrollCard } from '../components/HorizontalScroll';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { createCheckoutSession } from '../services/paymentService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ORDER = [
  { dbName: 'Credit Disputes & Credit Reporting', displayName: 'Credit Disputes', slug: 'credit-disputes' },
  { dbName: 'Business Credit & Business Setup', displayName: 'Business Credit', slug: 'business-credit' },
  { dbName: 'Court Motions & Legal Filings', displayName: 'Court Motions', slug: 'court-motions' },
  { dbName: 'Affidavits & Sworn Statements', displayName: 'Affidavits', slug: 'affidavits' },
  { dbName: 'Trust & Estate Planning', displayName: 'Trust & Estate', slug: 'trust-estate' },
  { dbName: 'Legal Notices & Demand Letters', displayName: 'Legal Notices', slug: 'legal-notices' },
  { dbName: 'Landlord-Tenant Disputes', displayName: 'Landlord-Tenant', slug: 'landlord-tenant' },
  { dbName: 'Lawsuits & Civil Complaints', displayName: 'Civil Lawsuit Templates & Filing Guide', slug: 'lawsuits' },
];

const BadgeTag = ({ badge }) => {
  if (!badge) return null;
  const config = {
    'Best Seller': { bg: 'bg-yellow-500', icon: <Star size={10} className="mr-1" /> },
    'Trending': { bg: 'bg-red-500', icon: <TrendingUp size={10} className="mr-1" /> },
    'Most Popular': { bg: 'bg-blue-500', icon: <Award size={10} className="mr-1" /> },
    'NEW': { bg: 'bg-emerald-500', icon: <Flame size={10} className="mr-1" /> },
  };
  const c = config[badge] || { bg: 'bg-gray-500', icon: null };
  return (
    <span className={`${c.bg} text-white text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center shadow-sm`}>
      {c.icon}{badge}
    </span>
  );
};

const BrowseGuides = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const resp = await axios.get(`${API}/products`);
        setProducts(resp.data.products.filter(p => p.product_type === 'guide' || !p.product_type));
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Scroll to hash section after products load
  useEffect(() => {
    if (!loading && window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [loading]);

  const handleQuickBuy = async (productId) => {
    setPurchasing(productId);
    try {
      const session = await createCheckoutSession(productId);
      if (session.url) window.location.href = session.url;
    } catch { alert('Unable to process. Please try again.'); }
    finally { setPurchasing(null); }
  };

  const groupedByCategory = CATEGORY_ORDER.map(cat => ({
    ...cat,
    guides: products.filter(p => p.category === cat.dbName),
  })).filter(cat => cat.guides.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-red-700 via-orange-600 to-orange-500 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3" data-testid="browse-title">Browse All Guides</h1>
          <p className="text-base md:text-lg text-white/90">Instant access to step-by-step guides and real templates designed for real-world results.</p>
        </div>
      </section>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
        </div>
      )}

      {/* All Guides by Category */}
      {!loading && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-14" data-testid="browse-guides-content">
          {groupedByCategory.map((cat) => (
            <section key={cat.dbName} id={cat.slug}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-gray-900" data-testid={`category-heading-${cat.slug}`}>
                    {cat.displayName}
                    {cat.slug === 'lawsuits' && <span className="ml-2 text-lg">🔥</span>}
                  </h2>
                  <span className="bg-orange-100 text-orange-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                    {cat.guides.length} guides
                  </span>
                </div>
                <a href={`/category/${cat.slug}`} className="text-sm text-red-700 hover:text-red-800 font-medium flex items-center gap-1 transition-colors">
                  View All <ChevronRight size={14} />
                </a>
              </div>
              <HorizontalScroll>
                {cat.guides.map(product => (
                  <ScrollCard key={product.id} width="w-52 sm:w-60">
                    <div
                      className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200 hover:border-orange-300 h-full"
                      data-testid={`browse-product-${product.id}`}
                    >
                      <BookMockup productId={product.id} title={product.title} size="card" badge={getBadge(product)} />
                      <div className="p-3">
                        <h3 className="text-xs font-bold text-gray-900 mb-1 line-clamp-2 text-center">{product.title}</h3>
                        <div className="flex items-center justify-between mt-2">
                          <a
                            href={`/guide/${product.id}`}
                            className="text-xs text-orange-600 hover:text-orange-800 font-medium transition-colors"
                            data-testid={`view-details-${product.id}`}
                          >
                            View Details
                          </a>
                          <button
                            className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors shadow-sm"
                            onClick={() => window.location.href = `/guide/${product.id}`}
                            disabled={purchasing === product.id}
                            data-testid={`buy-btn-${product.id}`}
                          >
                            {purchasing === product.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </ScrollCard>
                ))}
              </HorizontalScroll>
            </section>
          ))}
        </div>
      )}

      <Footer />
      <ExitIntentPopup />
    </div>
  );
};

export default BrowseGuides;
